// The blocking popup that fires when a follow-up enters its trigger window.
// Source CRM uses a gradient-heavy antd modal; we lean into the TG palette
// instead. Two actions: snooze (15 min) or mark done.

import React from "react";
import { Modal } from "antd";
import { Bell, Clock, Moon, Check } from "lucide-react";
import { Button } from "../atoms";

function formatTime(d) {
  return new Date(d).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

export default function ReminderPopup({ reminder, settings, onSnooze, onMarkDone, busy }) {
  if (!reminder) return null;
  const scheduled = new Date(reminder.scheduledAt);
  const before = settings?.beforeGapMinutes || 120;
  const after = settings?.afterGapMinutes || 120;
  const windowStart = new Date(scheduled.getTime() - before * 60 * 1000);
  const windowEnd = new Date(scheduled.getTime() + after * 60 * 1000);

  return (
    <Modal
      open
      centered
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      width={440}
      zIndex={2000}
      styles={{
        mask: { backdropFilter: "blur(6px)", background: "rgba(15, 15, 30, 0.55)" },
        content: { padding: 0, borderRadius: 20, overflow: "hidden", background: "var(--color-bg-surface)" },
        body: { padding: 0 },
      }}
    >
      <div style={{ padding: 28, color: "var(--color-text-primary)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 8,
              background: "var(--crm-primary)",
              color: "var(--crm-primary-text, #fff)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 10px 24px -8px rgba(99,102,241,0.45)",
            }}
          >
            <Bell size={22} />
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.16em",
                color: "var(--crm-primary)",
              }}
            >
              Follow-up reminder
            </div>
            <div
              style={{
                fontSize: 18,
                fontWeight: 600,
                marginTop: 2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {reminder.leadName}
            </div>
            {reminder.brandName && (
              <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 1 }}>
                {reminder.brandName}
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
            padding: 14,
            borderRadius: 8,
            background: "var(--color-bg-muted)",
            border: "1px solid var(--color-border-subtle)",
            marginBottom: 18,
          }}
        >
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.14em", color: "var(--color-text-secondary)", marginBottom: 4 }}>
              Scheduled
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 600 }}>
              <Clock size={13} style={{ color: "var(--crm-primary)" }} />
              {formatTime(scheduled)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.14em", color: "var(--color-text-secondary)", marginBottom: 4 }}>
              Window
            </div>
            <div style={{ fontSize: 14 }}>
              {formatTime(windowStart)} – {formatTime(windowEnd)}
            </div>
          </div>
        </div>

        {reminder.snoozeCount > 0 && (
          <div style={{ fontSize: 12, color: "var(--color-text-secondary)", textAlign: "center", marginBottom: 14 }}>
            Snoozed {reminder.snoozeCount} time{reminder.snoozeCount === 1 ? "" : "s"} already.
          </div>
        )}

        <div style={{ display: "flex", gap: 10 }}>
          <Button
            variant="secondary"
            size="md"
            onClick={onSnooze}
            disabled={busy}
            leftIcon={<Moon size={14} />}
            style={{ flex: 1, height: 44 }}
          >
            Snooze 15m
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={onMarkDone}
            disabled={busy}
            leftIcon={<Check size={14} />}
            style={{ flex: 1, height: 44 }}
          >
            Mark done
          </Button>
        </div>
      </div>
    </Modal>
  );
}
